import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const referralApi = createApi({
  reducerPath: "referralApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Referrals", "ReferralStats"],
  endpoints: (builder) => ({
    // Get all referrals
    getReferrals: builder.query({
      query: (params = {}) => ({
        url: "/referrals",
        method: "GET",
        params,
      }),
      providesTags: ["Referrals"],
      transformResponse: (response) => ({
        referrals: response.data || [],
        total: response.total || 0,
      }),
    }),

    // Get referral stats
    getReferralStats: builder.query({
      query: () => ({
        url: "/referrals/stats",
        method: "GET",
      }),
      providesTags: ["ReferralStats"],
      transformResponse: (response) => response.data || {
        total: 0,
        totalShares: 0,
        favorites: 0,
        categories: {},
      },
    }),

    // Get referrals by category
    getReferralsByCategory: builder.query({
      query: (category) => ({
        url: `/referrals/category/${category}`,
        method: "GET",
      }),
      providesTags: (result, error, category) => [
        { type: "Referrals", id: category },
      ],
      transformResponse: (response) => response.data || [],
    }),

    // Get single referral
    getReferralById: builder.query({
      query: (id) => ({
        url: `/referrals/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Referrals", id }],
      transformResponse: (response) => response.data,
    }),

    // Create referral
    createReferral: builder.mutation({
      query: (referralData) => ({
        url: "/referrals",
        method: "POST",
        body: referralData,
      }),
      invalidatesTags: ["Referrals", "ReferralStats"],
    }),

    // Update referral
    updateReferral: builder.mutation({
      query: ({ id, ...referralData }) => ({
        url: `/referrals/${id}`,
        method: "PUT",
        body: referralData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Referrals", id },
        "Referrals",
        "ReferralStats",
      ],
    }),

    // Delete referral
    deleteReferral: builder.mutation({
      query: (id) => ({
        url: `/referrals/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Referrals", "ReferralStats"],
    }),

    // Toggle favorite
    toggleFavorite: builder.mutation({
      query: (id) => ({
        url: `/referrals/${id}/favorite`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Referrals", id },
        "Referrals",
        "ReferralStats",
      ],
    }),

    // Track share
    trackShare: builder.mutation({
      query: ({ id, method }) => ({
        url: `/referrals/${id}/track-share`,
        method: "POST",
        body: { method },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Referrals", id },
        "ReferralStats",
      ],
    }),

    // Generate message
    generateMessage: builder.query({
      query: ({ id, agentName }) => ({
        url: `/referrals/${id}/message`,
        method: "GET",
        params: { agentName },
      }),
      transformResponse: (response) => response.data?.message || "",
    }),

    // Send referral email (get email data)
    sendReferralEmail: builder.mutation({
      query: ({ id, recipientEmail, recipientName, agentName }) => ({
        url: `/referrals/${id}/send-email`,
        method: "POST",
        body: { recipientEmail, recipientName, agentName },
      }),
    }),
  }),
});

export const {
  useGetReferralsQuery,
  useGetReferralStatsQuery,
  useGetReferralsByCategoryQuery,
  useGetReferralByIdQuery,
  useCreateReferralMutation,
  useUpdateReferralMutation,
  useDeleteReferralMutation,
  useToggleFavoriteMutation,
  useTrackShareMutation,
  useGenerateMessageQuery,
  useLazyGenerateMessageQuery,
  useSendReferralEmailMutation,
} = referralApi;
